import * as React from 'react';
import { addQueryParameters, IQueryParam, StringUtil } from '@fnv-core/utils';
import { IFormConfig } from '../../../interfaces/FormConfig';
import {
    BaseRelatedViewModel,
    KenticoFormViewmodel,
    KenticoPageDefinition,
    KenticoHandigeLinkBlockViewmodel,
    KenticoRichTextBlockViewmodel,
    KenticoPaymentBlockViewmodel,
    KenticoLidZoekenViewmodel,
    KenticoIdealBankSelectorBlockViewmodel,
    KenticTipBlockViewmodel,
    KenticDigitaleLedenPasBlockViewmodel,
    KenticoMijnFnvLinkBlockViewmodel,
    KenticoProfielFotoBlockViewmodel,
    KenticoMijnFnvViewPageBlockViewmodel,
    KenticoConsentDetailViewmodel,
} from '../interfaces/PageInterfaces';
import { widgetClassNames } from './PageHelpers';
import { KenticoFormContainer } from '../../../containers/KenticoFormContainer';
import { DashboardPage } from '../DashboardPage';
import { EditDataPage } from '../EditDataPage';
import { ViewDataPage } from '../ViewDataPage';
import { WebshopOverviewPage } from '../WebshopOverviewPage';
import { WebshopShoppingCartPage } from '../WebshopShoppingCartPage';
import { WebshopProductDetailPage } from '../WebshopProductDetailPage';
import { CTAViewDataPage } from '../CTAViewDataPage';
import { DigitaleLedenpasPage } from '../DigitaleLedenpasPage';
import { 
  MijnFNVHandigeLinks,
  MijnFNVContent,
  MijnFNVProfielFoto,
  MijnFNVPayment,
  MijnFNVIdealBankSelector,
  MijnFNVTipBlock,
  MijnFNVDigitaleLedenPasBlock,
  MijnFNVLinkBlock,
  MijnFNVViewPageBlock,
  MijnFNVLidZoekenResults,
  MijnFNVLidZoekenZoeker,
  MijnFNVLidZoekenLoadMore,
  MijnFNVConsentBlock,
} from '../../widgets';
import { StringHelpers } from '../../../helpers/StringHelpers';
import { FormConfigHelpers } from '../../../formcontrols/helpers/FormConfigHelpers';

function getFormConfigFromKenticoForm(formDefinition: any, orgFormConfig : IFormConfig): IFormConfig {

    // FormConfig is a "sensitive" way to extend or override a kentico form definition,
    // so, the FormConfig needs to be managed by development and follow OTAP
    // A FormConfig on a kentico form overrides the form-config on a form-block
    // .. in a later stage the form-config on a form-block will be removed completely
    let formConfig : IFormConfig;
    if (formDefinition.form && formDefinition.form.field) {
        if (formDefinition.form && formDefinition.form.field) {
            formDefinition.form.field.map((field) => {
                let restAPIEndpoint = "";
                if (field.settings && field.settings.RESTAPIEndpoint) {
                    restAPIEndpoint = field.settings.RESTAPIEndpoint;
                }

                if (field.settings && field.settings.FormConfig) {                
                    const formDefinedFormConfig = JSON.parse(field.settings.FormConfig) as IFormConfig;
                    if (orgFormConfig &&
                        orgFormConfig.thanksPageUrl !== undefined) {
                        formDefinedFormConfig.thanksPageUrl = orgFormConfig.thanksPageUrl;
                    }

                    if (orgFormConfig && orgFormConfig.kenticoFormName !== undefined) {
                        formDefinedFormConfig.kenticoFormName = orgFormConfig.kenticoFormName;
                    }
                      
                    if (orgFormConfig && orgFormConfig.submitButtonCaption !== undefined) {
                        formDefinedFormConfig.submitButtonCaption = orgFormConfig.submitButtonCaption;
                    }
                    if (orgFormConfig && orgFormConfig.LogoutPopupTitle !== undefined) {
                        formDefinedFormConfig.LogoutPopupTitle = orgFormConfig.LogoutPopupTitle;
                    }
                    if (orgFormConfig && orgFormConfig.LogoutPopupDescription !== undefined) {
                        formDefinedFormConfig.LogoutPopupDescription = orgFormConfig.LogoutPopupDescription;
                    }
                    if (orgFormConfig && orgFormConfig.noAutoGenerateSummary !== undefined) {
                        if (!(formDefinedFormConfig as any).useNoAutoGenerateSummaryFromForm) {
                            formDefinedFormConfig.noAutoGenerateSummary = orgFormConfig.noAutoGenerateSummary;
                        }
                    }
                    if (orgFormConfig && orgFormConfig.popupOK !== undefined) {
                        formDefinedFormConfig.popupOK = orgFormConfig.popupOK;
                    }
                    if (orgFormConfig && orgFormConfig.onSuccessPopupMessage !== undefined) {
                        formDefinedFormConfig.onSuccessPopupMessage = orgFormConfig.onSuccessPopupMessage;
                    }   
                    if (orgFormConfig && orgFormConfig.onSuccessPopupDescription !== undefined) {
                      formDefinedFormConfig.onSuccessPopupDescription = orgFormConfig.onSuccessPopupDescription;
                    }       

                    formConfig = formDefinedFormConfig;
                    formConfig.fillViaApi = formConfig.fillViaApi || restAPIEndpoint;
                    formConfig.postUrl = formConfig.postUrl || restAPIEndpoint;

                    // Merge procesHandlers if it exists in formConfig and orgFormConfig
                    if (formConfig.procesHandlers !== undefined || orgFormConfig?.procesHandlers !== undefined){
                      formConfig.procesHandlers = StringUtil.combineStringOrStringArrayProps(formConfig.procesHandlers, orgFormConfig?.procesHandlers);
                    }
                  
                    if (field.settings.SummaryValueBelowFields) {
                        formConfig.summaryValueBelowFields = field.settings.SummaryValueBelowFields === "True";
                    }
                }
            });
        }
    } 
    return formConfig;   
}

function handleFormConfig(viewModel: KenticoFormViewmodel, 
  viewModelformConfig: IFormConfig,
  additionalId: string = '') {
  // Respect the values from formConfig as override, but if empty, use values in viewModel
  const formConfigFromKenticoForm = getFormConfigFromKenticoForm(viewModel.FormDefinition, viewModelformConfig) || viewModelformConfig;
  let formConfig = StringHelpers.replaceLinkCodeNamesByUrls(viewModel.MijnFnvFormLinks, formConfigFromKenticoForm)
    || FormConfigHelpers.getDefaultFormConfig(viewModel.FormName, viewModel.Titel);
  
  if (viewModel.RestApiEndpoint) {
    formConfig.fillViaApi = formConfig.postUrl = viewModel.RestApiEndpoint;
  }
      
  const onSubmitSuccessLink = getSubmitSuccessLink(viewModel, additionalId);

  formConfig.reactRouteOnFailure = viewModel.OnSubmitFailureLink || formConfig.reactRouteOnFailure;
  formConfig.reactRouteOnSuccess = StringHelpers.replacePropertiesInUrl(
          formConfig.reactRouteOnSuccess, 
          { OnSubmitSuccessLink: onSubmitSuccessLink || ''}
      ) || onSubmitSuccessLink;

  return formConfig;
}

/**
 * Gets the successlink based on the given viewModel and additionalId
 * @param viewModel The viewModel
 * @param additionalId An optional additional id to include in the success url
 * @returns An url to navigate to after submit success.
 */
function getSubmitSuccessLink(viewModel: KenticoFormViewmodel, additionalId?: string): string {
  let onSubmitSuccessLink = viewModel.OnSubmitSuccessLink || '';
  const queryParams: IQueryParam[] = [];
  if (viewModel.IncludeAdditionalIdInSubmitLink && additionalId) {
    queryParams.push({ key: 'additionalId', value: additionalId });
  }

  if (viewModel.IncludeCurrentPageAsBackAndSubmitUrl) {
    const gobackUrl = (location.hash || '#').slice(1);
    queryParams.push({ key: 'gobackUrl', value: gobackUrl });
  }

  return addQueryParameters(onSubmitSuccessLink, queryParams);
}

export function createFormWidget(
    viewModel: KenticoFormViewmodel,
    additionalCssClass: string,
    index: number,
    pageType: string,
    additionalId : string = "",
    onPopupOkClick?: () => Promise<any>
) {
    // Use the values from Kentico, unless formconfig has overrides
    const formConfig = handleFormConfig(viewModel, viewModel.FormConfig, additionalId) || {} as IFormConfig;
    let editMode = viewModel.editMode;
    if (formConfig && formConfig.enableFormAsWidget === true) {
        editMode = true;
    }

    const formDefinition = StringHelpers.replaceLinkCodeNamesByUrls(viewModel.MijnFnvFormLinks, viewModel.FormDefinition);
    let OnSubmitSuccessButtonLink1 = viewModel.OnSubmitSuccessButton1Link || "";
    let OnSubmitSuccessButtonLink2 = viewModel.OnSubmitSuccessButton2Link || "";

    if (formConfig.reactRouteOnSuccess) {
        formConfig.orgReactRouteOnSuccess = formConfig.reactRouteOnSuccess;
    }

    return (
        <KenticoFormContainer
            key={'form-widget' + index}
            sidebarRef={viewModel.sidebarRef}
            stepsContainerRef={viewModel.stepsContainerRef}
            editMode={editMode}
            layoutType={viewModel.layoutType}
            formConfig={formConfig}
            formDefinitions={formDefinition}
            additionalCssClass={additionalCssClass}
            withLinkToFormPage={viewModel.withLinkToFormPage}
            formName={viewModel.FormName}
            formType={viewModel.FormType}
            formTitle={viewModel.Titel || ""}
            pageType={pageType}
            nextLink={viewModel.NextLink || viewModel.EditPageLink}
            linkText={viewModel.LinkText}
            summaryIntro={viewModel.SummaryIntro}
            includeCurrentPageAsBackAndSubmitUrl={viewModel.IncludeCurrentPageAsBackAndSubmitUrl}
            backTitleForCurrentPageBackLink={viewModel.backTitleForCurrentPageBackLink}
            onSuccessPopupMessage={viewModel.OnSuccessPopupMessage}
            onSuccessPopupMessageVisualCheck={viewModel.OnSuccessPopupMessageVisualCheck}
            onSuccessPopupDescription={viewModel.OnSuccessPopupDescription}
            onSubmitSuccessButton1Link={OnSubmitSuccessButtonLink1}
            onSubmitSuccessButton1LinkText={viewModel.OnSubmitSuccessButton1LinkText || ""}
            onSubmitSuccessButton2Link={OnSubmitSuccessButtonLink2}
            onSubmitSuccessButton2LinkText={viewModel.OnSubmitSuccessButton2LinkText || ""}

            errorMessageTitle={viewModel.ErrorMessageTitle}
            errorMessageDescription={viewModel.ErrorMessageDescription}
            errorMessagePhoneNumberInfo={viewModel.ErrorMessagePhoneNumberInfo}
            errorMessagePhoneNumber={viewModel.ErrorMessagePhoneNumber}
            errorMessageEmailInfo={viewModel.ErrorMessageEmailInfo}
            errorMessageEmail={viewModel.ErrorMessageEmail}
            errorMessageButtonText={viewModel.ErrorMessageButtonText}
            onPopupOkClick={onPopupOkClick}
        />
    );
}

function createLidZoekenWidget(
    viewModel: KenticoLidZoekenViewmodel,
    additionalCssClass: string,
    index: number,
    pageType: string
) {
      
    const searchMemberFormConfig = getFormConfigFromKenticoForm(viewModel.SearchMemberForm, viewModel.SearchMemberFormConfig) || viewModel.SearchMemberFormConfig;
    const searchMemberLoadMoreFormConfig = (viewModel.SearchMemberLoadMoreForm !== undefined && viewModel.SearchMemberLoadMoreFormConfig !== undefined) 
      ? (getFormConfigFromKenticoForm(viewModel.SearchMemberLoadMoreForm, viewModel.SearchMemberLoadMoreFormConfig) || viewModel.SearchMemberLoadMoreFormConfig) 
      : undefined;
    const memberDetailsFormConfig = getFormConfigFromKenticoForm(viewModel.MemberDetailsForm, viewModel.MemberDetailsFormConfig) || viewModel.MemberDetailsFormConfig;

    return (
        <React.Fragment key={'form-widget' + index}>
            <h2>{viewModel.Titel}</h2>
            <MijnFNVLidZoekenZoeker
                editMode={viewModel.editMode}
                formConfig={searchMemberFormConfig}
                formDefinitions={viewModel.SearchMemberForm}
                additionalCssClass={additionalCssClass}
                formName={viewModel.SearchMemberFormName}
                pageType={pageType}
            />
            <MijnFNVLidZoekenResults
                formConfig={memberDetailsFormConfig}
                formDefinitions={viewModel.MemberDetailsForm}
                pageType={pageType}
                formName={viewModel.MemberDetailsFormName}
                links={viewModel.MijnFnvLinks}
                memberFoundText={viewModel.MemberFoundText}
                memberFoundPluralText={viewModel.MemberFoundPluralText}
                memberNotFoundText={viewModel.MemberNotFoundText}
                memberNotFoundExplanation={viewModel.MemberNotFoundExplanation}
                moreThenMaxResultsFoundText={viewModel.MoreThenMaxResultsFoundText}
            ></MijnFNVLidZoekenResults>
            {searchMemberLoadMoreFormConfig !== undefined &&
              <MijnFNVLidZoekenLoadMore
                editMode={viewModel.editMode}
                formConfig={searchMemberLoadMoreFormConfig}
                formDefinitions={viewModel.SearchMemberLoadMoreForm}
                additionalCssClass={additionalCssClass}
                formName={viewModel.SearchMemberLoadMoreFormName}
                pageType={pageType}
              />
            }
        </React.Fragment>
    );
}

function createLinkBlockWidget(
    viewModel: KenticoHandigeLinkBlockViewmodel,
    additionalCssClass: string,
    index: number,
) {
    return (
        <MijnFNVHandigeLinks
            key={'handige-links-widget' + index}
            title={viewModel.Titel}
            MijnFnvLinks={viewModel.MijnFnvLinks}
            additionalCssClass={additionalCssClass}
        ></MijnFNVHandigeLinks>
    );
}

function createMijnFnvLinkBlockWidget(
    viewModel: KenticoMijnFnvLinkBlockViewmodel,
    additionalCssClass: string,
    index: number,
) {
    return (
        <MijnFNVLinkBlock
            key={'mijn-fnv-links-block-widget' + index}
            mainTitle={viewModel.MainTitle}
            layout={viewModel.Layout}
            title={viewModel.Title}
            intro={viewModel.Intro}
            link={viewModel.Link}
            linkButtonText={viewModel.LinkButtonText}
            visible={viewModel.Visible}
            openLinkInNewTab={viewModel.OpenLinkInNewTab}
            additionalCssClass={additionalCssClass}
            ctaImage={viewModel.CTAImage}
            headerImage={viewModel.HeaderImage}
        ></MijnFNVLinkBlock>
    );
}

function createContentBlockWidget(
    viewModel: KenticoRichTextBlockViewmodel,
    additionalCssClass: string,
    index: number,
) {
    return (
        <MijnFNVContent
            key={'content-widget' + index}
            content={viewModel.Content}
            cssClass={`${(viewModel.CssClass || '')} ${additionalCssClass}`}
            title={viewModel.Titel}
        ></MijnFNVContent>
    );
}

function createProfielFotoBlockWidget(
    viewModel: KenticoProfielFotoBlockViewmodel,
    index: number,
) {
    return (
        <MijnFNVProfielFoto
            key={'profiel-foto-widget' + index}
            formPageUrl={viewModel.FormulierPagina}
            photoUrl={viewModel.ProfilePhotoUrl}
        ></MijnFNVProfielFoto>
    );
}


function createPaymentBlockWidget(
    viewModel: KenticoPaymentBlockViewmodel,
    index: number,
) {
    return (
        <MijnFNVPayment
            key={'content-widget' + index}
            title={viewModel.Titel}
            payButtonText={viewModel.PayButtonText}
            payButtonUrl={viewModel.PayButtonUrl}
            totalAmountText={viewModel.TotalAmountText}
            payXMonthContributionText={viewModel.PayXMonthContributionText}
            intro={viewModel.Intro}
            openContributionText={viewModel.OpenContributionText}
            noOpenContributionText={viewModel.NoOpenContributionText}
            contributionTitle={viewModel.ContributionTitle}
            contributionInformation={viewModel.ContributionInformation}
            payInAdvanceTitle={viewModel.PayInAdvanceTitle}
            payInAdvanceInformation={viewModel.PayInAdvanceInformation}
        ></MijnFNVPayment>
    );
}

function createIdealBankKeuzeBlockWidget(
    viewModel: KenticoIdealBankSelectorBlockViewmodel,
    index: number,
) {
    return (
        <MijnFNVIdealBankSelector
            key={'content-widget' + index}
            title={viewModel.Titel}
            intro={viewModel.Intro}
            bankSelectorLabel={viewModel.BankSelectorLabel}
            selectBankPlaceholder={viewModel.SelectBankPlaceholder}
            paymentButtonTitle={viewModel.PaymentButtonTitle}
        ></MijnFNVIdealBankSelector>
    );
}

function createTipBlockWidget(
    viewModel: KenticTipBlockViewmodel,
    additionalCssClass: string,
    index: number,
) {
    return (
        <MijnFNVTipBlock
            key={'content-widget' + index}
            text={viewModel.TipText}
            icon={viewModel.TipIcon}
            layout={viewModel.TipLayout}
            additionalCssClass={additionalCssClass}
        ></MijnFNVTipBlock>
    );
}

function createDigitaleLedenPasBlockWidget(
    viewModel: KenticDigitaleLedenPasBlockViewmodel,
    index: number,
) {
    return (
        <MijnFNVDigitaleLedenPasBlock
            key={'content-widget' + index}
            apiUrl={viewModel.ApiUrl}
            title={viewModel.Title}
            intro={viewModel.Intro}
        ></MijnFNVDigitaleLedenPasBlock>
    );
}

function createViewPageBlockWidget(
  viewModel: KenticoMijnFnvViewPageBlockViewmodel,
  index: number,
) {
  return (
    <MijnFNVViewPageBlock
      key={'content-widget' + index}
      title={viewModel.Titel}
      subtitle={viewModel.Subtitle}
      url={viewModel.UrlPath}
    ></MijnFNVViewPageBlock>
  );
}

function createConsentBlockWidget(
  viewModel: KenticoConsentDetailViewmodel,
  index: number,
) {
  return (
    <MijnFNVConsentBlock
      key={'content-widget' + index}
      title={viewModel.Title}
      description={viewModel.Description}
      consentId={viewModel.DocumentGuid}
      consent={viewModel.IsOptedIn}
      />
  );
}

export class PageRenderHelpers {
    static renderWidget(
        widget: BaseRelatedViewModel,
        additionalCssClass: string,
        index: number,
        pageType: string,
        additionalId : string = ""
    ) {
        switch (widget.ClassName) {
            case widgetClassNames.formClass:
                return createFormWidget(
                    widget as KenticoFormViewmodel,
                    additionalCssClass + (pageType == "dashboard" || pageType == "edit" || pageType == "view" ? " mijn-fnv__widget" : ""),
                    index,
                    pageType,
                    additionalId
                );
            case widgetClassNames.linkBlockClass:
                return createLinkBlockWidget(
                    widget as KenticoHandigeLinkBlockViewmodel,
                    additionalCssClass + (pageType == "dashboard" ? " mijn-fnv__widget" : ""),
                    index,
                );
            case widgetClassNames.downloadBlockClass:    
            case widgetClassNames.contentBlockClass:
                return createContentBlockWidget(
                    widget as KenticoRichTextBlockViewmodel,
                    additionalCssClass + (pageType == "dashboard" ? " mijn-fnv__widget" : ""),
                    index,
                );
            case widgetClassNames.profielFotoBlockClass:
                return createProfielFotoBlockWidget(
                    widget as KenticoProfielFotoBlockViewmodel,
                    index,
                );    
            case widgetClassNames.paymentBlockClass:
                return createPaymentBlockWidget(
                    widget as KenticoPaymentBlockViewmodel,
                    index,
                );
            case widgetClassNames.idealBankKeuzeBlockClass:
                return createIdealBankKeuzeBlockWidget(
                    widget as KenticoIdealBankSelectorBlockViewmodel,
                    index,
                );
            case widgetClassNames.tipBlockClass:
                return createTipBlockWidget(
                    widget as KenticTipBlockViewmodel,
                    additionalCssClass + (pageType == "dashboard" ? " mijn-fnv__widget" : ""),
                    index,
                );
            case widgetClassNames.digitaleLedenPasBlockClass:
                return createDigitaleLedenPasBlockWidget(
                    widget as KenticDigitaleLedenPasBlockViewmodel,
                    index,
                );
            case widgetClassNames.mijnFnvLinkBlockClass:
                return createMijnFnvLinkBlockWidget(
                    widget as KenticoMijnFnvLinkBlockViewmodel,
                    additionalCssClass + (pageType == "dashboard" ? " mijn-fnv__widget" : ""),
                    index,
                );
            case widgetClassNames.lidZoekenBlockClass:
                return createLidZoekenWidget(
                    widget as KenticoLidZoekenViewmodel,
                    additionalCssClass + (pageType == "dashboard" ? " mijn-fnv__widget" : ""),
                    index,
                    pageType
                );
            case widgetClassNames.mijnFnvViewPageBlockClass:
              return createViewPageBlockWidget(
                widget as KenticoMijnFnvViewPageBlockViewmodel,
                index,
              );
            case widgetClassNames.consentBlockClass:
              return createConsentBlockWidget(
                widget as KenticoConsentDetailViewmodel,
                index,
              );
        }

        return (
            <div key={'widget' + index} className="fnv-form__container-form widget">
                <div className="form__react">
                    <h2 className="form__panel-title">{widget.ClassName}</h2>
                </div>
            </div>
        );
    }

    static getPageTypeComponent(
        pageType: string,
        kenticoPage: KenticoPageDefinition,
        pathName: string,
        kenticoPageType: string
    ) {
        let pageTypeToComponentMap = {
            dashboard: DashboardPage,
            edit: EditDataPage,
            view: ViewDataPage,
            ctaview: CTAViewDataPage,
            digitaleledenpas: DigitaleLedenpasPage,
            webshop: WebshopOverviewPage,
            webshopProduct: WebshopProductDetailPage,
            webshopShoppingCart: WebshopShoppingCartPage
        };
        let layoutType = "";
        let sidebarHtml = "";
        let showTitleBlock = false;

        if (kenticoPage !== null &&
            kenticoPage.MijnFnvEditPagina) {

            if (!!kenticoPage.MijnFnvEditPagina.ModernLayout) {
                layoutType = "modern";
                sidebarHtml = "<span></span>";
                showTitleBlock = true;
            }

            if (!!kenticoPage.MijnFnvEditPagina.HidePageTitle) {
                showTitleBlock = false;
            }
        }

        let Component = pageTypeToComponentMap[pageType];
        if (Component !== undefined) {
            return (
                <Component
                    kenticoPage={kenticoPage}
                    pathName={pathName}
                    kenticoPageType={kenticoPageType}
                    layoutType={layoutType}                    
                    sideBarHtml={sidebarHtml}
                    showTitleBlock={showTitleBlock}
                />
            );
        }
        return null;
    }

    static getCssClassForEditPageWidth(pageWidth: string, hasSideBar: boolean) {
        // ignore sidebar here : on editpage it is outside the container that uses these classes
        //if (hasSideBar === true) {
        //    return 'col-md-8';
        //}
        if (pageWidth === "breed") {
          return "col-md-12";
        } else
        if (pageWidth === "smal") {
          return "col-md-8 p-0 px-md-2";
        }
        return "col-md-10";
    }

    static getCssClassForPageWidthViewPageType(pageWidth: string, hasSideBar : boolean, hasActionsAndHeadingForm : boolean) {
      if (hasSideBar === true) {
        return "col-md-8";
      }

      if (hasActionsAndHeadingForm === true) {
        return "col-md-8";
      }

      if (pageWidth === "breed") {
        return "col-md-10";
      } else
      if (pageWidth === "smal") {
        return "col-md-7";
      }
      return "col-md-8";
    }
}
